import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getMotifs } from '@/api/directSale';
import { useImageOperations } from '@/hooks/useImageOperations';
import { useDesignStore } from '@/stores/designStore';
import { useProductSelectionStore } from '@/stores/productSelectionStore';

/**
 * The motif tool from the design mock: search field, three-column grid,
 * license footnote. Clicking a motif places it on the active print position.
 *
 * The motif id doubles as the placed object's assetId (its file endpoint is
 * permanent), so the object counts as a completed asset — placed with a
 * `temp_` id it would be silently dropped by the serializer's
 * filterTempAssets and never persist or unlock the checkout.
 *
 * Search filters client-side across both language names — the library is
 * curated and small; a server round-trip per keystroke buys nothing.
 */
export function DirectSaleMotifPanel({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [placeFailed, setPlaceFailed] = useState(false);
  const { addImageFromUrl, isLoading: isPlacing } = useImageOperations();

  const {
    data: motifs,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['direct-sale', 'motifs'],
    queryFn: getMotifs,
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return motifs ?? [];
    return (motifs ?? []).filter(
      (m) =>
        m.name.toLowerCase().includes(term) ||
        m.nameEn.toLowerCase().includes(term)
    );
  }, [motifs, search]);

  const handlePick = async (motifId: string, fileUrl: string) => {
    setPlaceFailed(false);
    // The image fetch can take seconds; if the customer switches the print
    // position meanwhile, the resolved canvas.add would land on (and bill)
    // the NEW position — capture the origin and undo a stale placement.
    const originPosition = useProductSelectionStore.getState().activePosition;
    const placed = await addImageFromUrl(fileUrl, motifId, {
      placement: 'print-area-fitted',
    });

    if (!placed) {
      setPlaceFailed(true);
      return;
    }

    const currentPosition = useProductSelectionStore.getState().activePosition;
    if (currentPosition !== originPosition) {
      // Cross-store canvas access — a cleanup failure must not break the
      // panel (cf. docs/solutions/ui-bugs/wrong-product-mockup-edit-placement.md).
      try {
        const canvas = useDesignStore.getState().canvas;
        if (canvas) {
          canvas.remove(placed);
          canvas.discardActiveObject();
          canvas.requestRenderAll();
        }
      } catch {
        // The stray object stays; the customer can delete it manually.
      }
      setPlaceFailed(true);
      return;
    }

    onClose();
  };

  return (
    <div className="p-3.5">
      <div className="flex items-center gap-2 rounded-control border border-line px-3.5 py-2 text-mute focus-within:border-primary">
        <Search className="h-[15px] w-[15px] flex-none" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('directSale.design.motifPanel.search')}
          className="w-full bg-transparent text-[12.5px] text-ink-2 outline-none placeholder:text-mute"
        />
      </div>

      {isPending && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="aspect-square animate-pulse rounded-[10px] bg-paper-2"
            />
          ))}
        </div>
      )}

      {isError && (
        <p className="mt-3 text-xs text-ink-2">
          {t('directSale.design.motifPanel.loadFailed')}
        </p>
      )}

      {!isPending && !isError && (
        <>
          {filtered.length === 0 ? (
            <p className="mt-3 text-xs text-mute">
              {search.trim()
                ? t('directSale.design.motifPanel.empty')
                : t('directSale.design.motifPanel.none')}
            </p>
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {filtered.map((motif) => (
                <button
                  key={motif.id}
                  type="button"
                  disabled={isPlacing}
                  title={i18n.language === 'en' ? motif.nameEn : motif.name}
                  aria-label={
                    i18n.language === 'en' ? motif.nameEn : motif.name
                  }
                  onClick={() => void handlePick(motif.id, motif.fileUrl)}
                  className="flex aspect-square items-center justify-center rounded-[10px] border border-line bg-paper p-2 transition-all hover:border-primary disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <img
                    src={motif.fileUrl}
                    alt={i18n.language === 'en' ? motif.nameEn : motif.name}
                    crossOrigin="anonymous"
                    loading="lazy"
                    className="max-h-full max-w-full object-contain"
                  />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {placeFailed && (
        <p role="alert" className="mt-2.5 text-xs font-medium text-danger">
          {t('directSale.design.motifPanel.placeFailed')}
        </p>
      )}

      <p className="mt-2.5 text-[11px] text-mute">
        {t('directSale.design.motifPanel.license')}
      </p>
    </div>
  );
}
